import React, { useEffect, FC } from 'react'; // eslint-disable-line
import { useRouteMatch, useLocation, useParams } from 'react-router';
import { useSelector } from 'react-redux';
import styled, { useTheme } from 'styled-components';
import { up } from 'styled-breakpoints';
// @fenderdigital
import Spinner from '@fenderdigital/ui/Spinner';
import { ButtonBase as Button } from '@fenderdigital/ui/Button';
import { PointerLeft } from '@fenderdigital/ui/Icons';
import usePlayUser from '@fenderdigital/custom-hooks/hooks/usePlayUser';
// components
import HeadTitle from '../../../components/Head/HeadTitle';
import NewCardMenu from '../../../components/CardMenu';
import useBackHandler from '../../../components/BackButton/useBackHandler';
import ProgressList from '../../../components/ProgressList';
import SubscriptionCheck from '../../../components/SubscriptionCheck';
// lib
import { EventNames, trackPageEvents, getAlgoliaTracking } from '../../../lib/analytics';
// utils
import useDataLoader from '../../../utils/useDataLoader';
import { isLocked } from '../../../utils/access-utils';
import { slugPageTitle } from '../../../utils/string-utils';
// redux
import { RootState } from '../../../redux-types/root.redux-types';

const CourseBackground = styled.div`
  min-height: calc(100vh - 60px);
  background: rgb(240, 240, 240);
`;

const CourseHeader = styled.div`
  width: 100%;
  height: 67px;
  padding: 0px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${({ theme }): string => theme.colors.white};

  ${up('laptop')} {
    padding: 0px 32px;
  }
`;

const BackButtonContainer = styled.div`
  button {
    min-width: auto;
    width: auto;
    font-family: ${({ theme: { fonts } }): string => fonts.sourceSans};
    padding-left: 0;
    color: ${({ theme }): string => theme.colors.black};
    text-transform: none;

    &:hover {
      color: ${({ theme }): string => theme.colors.black};
    }
  }

  svg {
    margin-bottom: 2px;
    margin-right: ${({ theme: { spacing } }): string => spacing.sm3};
    fill: ${({ theme }): string => theme.colors.black};
  }
`;

const selector = (state: RootState) => {
  const { courses, myProgress, myPath } = state;
  const { slugs } = courses;
  const { lessons, courses: coursesProgress } = myProgress;
  const { watching } = courses;
  const { currentLesson } = watching;

  let pathLessons = [];
  if (myPath && myPath.flatLessons) {
    pathLessons = myPath.flatLessons;
  }

  return {
    coursesProgress,
    currentLesson,
    lessonProgress: lessons,
    myPath,
    myProgress,
    pathLessons,
    slugs,
  };
};

const Course: FC = () => {
  const location = useLocation();
  const match = useRouteMatch();
  const url = match?.url;
  const { courseSlug } = useParams();
  const { courseNumber } = location.state || {};

  const {
    coursesProgress,
    currentLesson,
    lessonProgress,
    myPath,
    myProgress,
    pathLessons,
    slugs,
  } = useSelector(selector);

  const courseData = slugs[courseSlug ?? ''];

  const { recentlyViewed } = myProgress || {};
  const { isFetching } = useDataLoader();
  const backHandler = useBackHandler();
  const { colors, fontSizes } = useTheme();
  const slugTitle = slugPageTitle(courseSlug ?? '');
  const pageTitle = `Fender Play Online Guitar Lessons - Learn How to Play Guitar - ${slugTitle}`;
  const { accessLevel: userAccessLevel, user } = usePlayUser();
  const courseAccessLevel = courseData?.['access-levels'];
  const isAvailable = !isLocked(courseAccessLevel, userAccessLevel);

  useEffect(() => {
    if (!isFetching) {
      trackPageEvents(EventNames.COURSE_DETAIL, {
        fc_id: user?.userID,
        ...getAlgoliaTracking('view', courseData?.id),
      });
    }
  }, [isFetching]);

  const markCompleteData = {
    'path-id': myPath?.id,
    'playlist-id': myPath?.playlists?.[courseNumber - 1]?.id,
    style: myPath?.style,
    title: myPath?.playlists?.[courseNumber - 1]?.title,
    instrument: myPath?.instrument,
  };

  if (isFetching)
    return (
      <div>
        <Spinner fullPage strokeBaseColor="black" strokeColor="transparent" />
      </div>
    );

  return (
    <CourseBackground>
      <HeadTitle title={pageTitle} />
      <CourseHeader>
        <BackButtonContainer>
          <Button isFluid variant="ghost" hoverColor="transparent" onClick={backHandler}>
            <span>
              <PointerLeft fill={colors.white} fontSize={fontSizes.f2} aria-hidden="true" />
              Back
            </span>
          </Button>
        </BackButtonContainer>
        <NewCardMenu
          courseData={courseData}
          testId="course-detail-card"
          markCompleteData={markCompleteData}
          isCourseHeader
          isCourse
          isAvailable={isAvailable}
        />
      </CourseHeader>
      <ProgressList
        url={url as string}
        courses={[courseData]}
        courseNumber={courseNumber}
        recentlyViewed={recentlyViewed}
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        currentLesson={currentLesson}
        lessonProgress={lessonProgress}
        pathLessons={pathLessons}
        coursesProgress={coursesProgress}
        view="courseDetail"
        markCompleteData={markCompleteData}
      />
    </CourseBackground>
  );
};

export default SubscriptionCheck(Course);
